import { measuredScores } from '../lib/client.js';
import { playMedia } from '../lib/media.js';
/**
 * The lesson session and its report card.
 *
 * This screen is where the anti-deadlock guarantees become visible to the learner
 * (docs/02-user-flows.md §3):
 *
 *   - a scoring failure shows the turn as unscored and the lesson carries on
 *   - a dead microphone degrades the session to text with an explanation, rather than
 *     leaving the learner holding a broken button
 *   - the report card's primary action is always the drill, which closes the loop
 */

import type { RouteResponse } from '@saysooner/api-client';
import {
  AppealControl,
  Button,
  DegradedBanner,
  DimensionsPanel,
  MicButton,
  NaturalAlternative,
  PhonemeTable,
  PlaybackRow,
  RemediationCard,
  ScoreRing,
  Skeleton,
  WithheldNotice,
  WordScoreText,
} from '@saysooner/ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { noTracking, type Track } from '../app-context.js';

type Turn = RouteResponse<'submitTurn'>['turn'];
type Report = RouteResponse<'completeSession'>['report'];

export interface LessonSessionProps {
  readonly lessonTitle: string;
  readonly lessonGoal: string;
  readonly lang: string;
  readonly turnIndex: number;
  readonly totalTurns: number;
  readonly currentTurn: Turn | null;
  readonly mode: 'voice' | 'text';
  readonly degradeReason: string | null;
  readonly micState:
    | 'idle'
    | 'listening'
    | 'processing'
    | 'success'
    | 'lowConfidence'
    | 'disabled'
    | 'noPermission';
  readonly micMode: 'hold' | 'toggle';
  readonly level: number;
  readonly busy: boolean;
  readonly onStartRecording: () => void;
  readonly onStopRecording: () => void;
  readonly onSubmitText: (text: string) => void;
  /**
   * Whether this turn is answered and waiting to be left.
   *
   * It is the machine's `turnFeedback` state, passed in rather than guessed. The guess
   * used to be `feedback !== null` — the presence of a *score* — which meant that when
   * scoring was unavailable the only control out of the turn never rendered and the
   * lesson could not be finished. That is the exact failure the lesson machine is built
   * to make impossible: it is total over all three scoring outcomes and advances on
   * every one of them, and the UI then gated the exit on one of them.
   *
   * Every score was `unavailable` on staging, because Azure has no key there, so every
   * lesson stopped on its first turn.
   */
  readonly turnAnswered: boolean;
  readonly onNextTurn: () => void;
  readonly onHint: () => void;
  readonly onTranslate: () => void;
  readonly onRepeat: () => void;
  readonly onSwitchToText: () => void;
  readonly onRetryVoice: () => void;
  readonly onExit: () => void;
  /**
   * Files the appeal. `consentToStore` decides only whether we keep the recording —
   * the appeal is filed either way, which the control says out loud.
   */
  readonly onAppeal: (consentToStore: boolean) => void;
  /** Reports the feedback itself as wrong, which is a different claim from an appeal. */
  readonly onReportFeedback?: (() => void) | undefined;
  /** The server's own answer to the appeal, so we do not paraphrase it. */
  readonly appealOutcome?: string | null | undefined;
  /** Opens minimal-pair practice for one sound. */
  readonly onPractisePhoneme?: ((phoneme: string) => void) | undefined;
  readonly hint: readonly { text: string; translation: string }[] | null;
  readonly translationVisible: boolean;
  readonly track?: Track | undefined;
}

export function LessonSession(props: LessonSessionProps): JSX.Element {
  const [text, setText] = useState('');
  const turn = props.currentTurn;
  const feedback = turn?.feedback ?? null;

  return (
    <main id="main" className="vl-page vl-lesson">
      <header className="vl-lesson__header">
        <button
          type="button"
          className="vl-icon-button"
          aria-label="Leave lesson"
          onClick={props.onExit}
        >
          ✕
        </button>
        <p className="vl-lesson__progress" aria-live="polite">
          Turn {Math.min(props.turnIndex + 1, props.totalTurns)} of {props.totalTurns}
        </p>
        <span className="vl-lesson__goal">{props.lessonGoal}</span>
      </header>

      {props.degradeReason !== null && (
        <DegradedBanner reason={props.degradeReason} onRetryVoice={props.onRetryVoice} />
      )}

      <section className="vl-lesson__transcript" aria-label="Conversation">
        {turn !== null && turn.tutorText.length > 0 && (
          <article className="vl-bubble vl-bubble--tutor">
            <p lang={props.lang}>{turn.tutorText}</p>
            {props.translationVisible && turn.tutorTranslation !== null && (
              <p className="vl-bubble__translation">{turn.tutorTranslation}</p>
            )}
            <PlaybackRow onPlayCoach={props.onRepeat} />
          </article>
        )}

        {turn?.learnerTranscript != null && (
          <article className="vl-bubble vl-bubble--learner">
            <p lang={props.lang}>{turn.learnerTranscript}</p>
            {feedback !== null && feedback.overall !== null && (
              <ScoreRing score={feedback.overall} size="sm" label="This turn" />
            )}
          </article>
        )}
      </section>

      {feedback !== null && (
        <TurnFeedback
          feedback={feedback}
          onAppeal={props.onAppeal}
          track={props.track}
          {...(props.onReportFeedback === undefined
            ? {}
            : { onReportFeedback: props.onReportFeedback })}
          {...(props.appealOutcome == null ? {} : { appealOutcome: props.appealOutcome })}
          {...(props.onPractisePhoneme === undefined
            ? {}
            : { onPractisePhoneme: props.onPractisePhoneme })}
        />
      )}

      {/* Scoring unavailable is stated plainly — and the lesson keeps going. */}
      {feedback !== null && feedback.status === 'unavailable' && (
        <p className="vl-lesson__unscored" role="status">
          We couldn&apos;t score that one — it&apos;ll be scored shortly. Carry on.
        </p>
      )}

      <section className="vl-lesson__input">
        {props.mode === 'voice' ? (
          <>
            <div className="vl-lesson__aids">
              <Button variant="ghost" size="sm" onClick={props.onHint}>
                ✨ Hint
              </Button>
              <Button variant="ghost" size="sm" onClick={props.onTranslate}>
                🌐 Translate
              </Button>
              <Button variant="ghost" size="sm" onClick={props.onSwitchToText}>
                ⌨ Text
              </Button>
            </div>

            {props.hint !== null && (
              <ul className="vl-hints">
                {props.hint.map((option) => (
                  <li key={option.text}>
                    <span lang={props.lang}>{option.text}</span>
                    <span className="vl-hints__translation">{option.translation}</span>
                  </li>
                ))}
              </ul>
            )}

            <MicButton
              state={props.micState}
              mode={props.micMode}
              level={props.level}
              size="lg"
              onStart={props.onStartRecording}
              onStop={props.onStopRecording}
            />
          </>
        ) : (
          <form
            className="vl-lesson__text-form"
            onSubmit={(event) => {
              event.preventDefault();
              if (text.trim().length === 0) return;
              props.onSubmitText(text.trim());
              setText('');
            }}
          >
            <label className="vl-field">
              <span className="vl-field__label">Your reply</span>
              <input
                className="vl-field__input"
                value={text}
                onChange={(event) => setText(event.target.value)}
                lang={props.lang}
                autoComplete="off"
              />
            </label>
            <Button type="submit" loading={props.busy} disabled={text.trim().length === 0}>
              Send
            </Button>
          </form>
        )}

        {props.turnAnswered && (
          <Button onClick={props.onNextTurn} loading={props.busy}>
            {props.turnIndex + 1 >= props.totalTurns ? 'Finish lesson' : 'Next turn'}
          </Button>
        )}
      </section>
    </main>
  );
}

export interface TurnFeedbackProps {
  readonly feedback: NonNullable<Turn['feedback']>;
  readonly onAppeal: (consentToStore: boolean) => void;
  readonly onReportFeedback?: (() => void) | undefined;
  readonly appealOutcome?: string | undefined;
  readonly onPractisePhoneme?: (phoneme: string) => void;
  readonly track?: Track | undefined;
}

/**
 * The feedback card. Composed from the market's best ideas, with our two additions:
 * the withheld state, and the appeal control (see docs/research/02-market-map.md §2).
 */
export function TurnFeedback({
  feedback,
  onAppeal,
  onReportFeedback,
  appealOutcome,
  onPractisePhoneme,
  track = noTracking,
}: TurnFeedbackProps): JSX.Element {
  const [appealed, setAppealed] = useState(feedback.appealedAt !== null);
  const [expanded, setExpanded] = useState<number | null>(null);

  // The score the learner was actually shown. How often this card appears with a
  // withheld status is the number the confidence-gating wedge is judged on, so the
  // withheld case is recorded too rather than returning early without an event.
  useEffect(() => {
    track('feedback_card_view', { score: feedback.overall });
  }, [feedback.overall, track]);

  if (feedback.status === 'withheld') {
    return <WithheldNotice reason={feedback.withheldReason} message={feedback.message} />;
  }

  return (
    <section className="vl-feedback" aria-label="Feedback on what you said">
      <WordScoreText
        words={feedback.words.map((word) => ({
          word: word.word,
          score: word.score,
          band: word.band,
        }))}
        onWordClick={
          feedback.hasPhonemeDetail
            ? (index) => {
                const word = feedback.words[index];
                if (word !== undefined) {
                  track('feedback_word_expanded', { word: word.word, score: word.score });
                }
                setExpanded(index);
              }
            : undefined
        }
      />

      <div className="vl-feedback__scores">
        <ScoreRing score={feedback.overall} size="md" label="Overall" />
        <DimensionsPanel dimensions={feedback.dimensions} />
      </div>

      <PlaybackRow
        {...(feedback.learnerAudioUrl !== null
          ? {
              onPlayLearner: () => {
                // Hearing yourself against the model is the comparison the whole card
                // is for, so whether anyone does it is worth knowing.
                track('feedback_compare_tapped', {});
                playAudio(feedback.learnerAudioUrl);
              },
            }
          : {})}
        {...(feedback.coachAudioUrl !== null
          ? {
              onPlayCoach: () => {
                track('feedback_compare_tapped', {});
                playAudio(feedback.coachAudioUrl);
              },
            }
          : {})}
        {...(feedback.coachSlowAudioUrl !== null
          ? { onPlayCoachSlow: () => playAudio(feedback.coachSlowAudioUrl) }
          : {})}
      />

      {/* `reduced` means the gate forbade phoneme claims; we say so rather than hiding it. */}
      {feedback.status === 'reduced' && feedback.message !== null && (
        <p className="vl-feedback__reduced" role="status">
          {feedback.message}
        </p>
      )}

      {feedback.hasPhonemeDetail && expanded !== null && feedback.words[expanded] !== undefined && (
        <PhonemeTable
          word={feedback.words[expanded].word}
          score={feedback.words[expanded].score}
          stressCorrect={feedback.words[expanded].stressCorrect}
          phonemes={feedback.words[expanded].phonemes}
          available
          {...(onPractisePhoneme !== undefined
            ? {
                onExplain: (phoneme: string) => {
                  track('feedback_explain_tapped', {});
                  track('feedback_phoneme_audio', { phoneme });
                  onPractisePhoneme(phoneme);
                },
                // The contrast the server computed and every client decoded and
                // discarded: "sounded like /j/" with no way to go hear the difference.
                onCompare: (phoneme: string) => {
                  track('feedback_compare_tapped', {});
                  onPractisePhoneme(phoneme);
                },
              }
            : {})}
        />
      )}

      {feedback.naturalAlternative !== null && (
        <NaturalAlternative
          text={feedback.naturalAlternative.text}
          {...(feedback.naturalAlternative.audioUrl !== null
            ? { onPlay: () => playAudio(feedback.naturalAlternative?.audioUrl ?? null) }
            : {})}
        />
      )}

      {feedback.remediation !== null && (
        <RemediationCard
          phonemes={feedback.remediation.phonemes}
          estimatedMinutes={feedback.remediation.estimatedMinutes}
          projectedAccuracyGain={feedback.remediation.projectedAccuracyGain}
          onStart={() => onPractisePhoneme?.(feedback.remediation?.phonemes[0] ?? '')}
        />
      )}

      {/* One control with two routes out, rather than two buttons calling the same
          handler — which is what this was, so a report and an appeal were
          indistinguishable. An appeal contests the score; a report says the
          explanation itself is wrong, which is a content problem and goes elsewhere.
          The competitor's top review theme is bad feedback with nowhere to say so. */}
      <AppealControl
        appealed={appealed}
        {...(appealOutcome === undefined ? {} : { outcome: appealOutcome })}
        {...(onReportFeedback === undefined
          ? {}
          : {
              onReport: () => {
                track('feedback_reported', { reason: 'unhelpful' });
                onReportFeedback();
              },
            })}
        onAppeal={(consentToStore) => {
          // The appeal rate is the single most important number about our scoring: the
          // competitor's loudest complaint is false negatives with no recourse, and
          // this is the measurement that tells us whether we are repeating it.
          const worst = [...feedback.words].sort((a, b) => a.score - b.score)[0];
          track('feedback_appeal', {
            word: worst?.word ?? null,
            phoneme: worst?.phonemes?.[0]?.expected ?? null,
          });
          setAppealed(true);
          onAppeal(consentToStore);
        }}
      />
    </section>
  );
}

export interface ReportCardScreenProps {
  readonly report: Report | null;
  readonly loading: boolean;
  readonly onDrill: () => void;
  readonly onTranscript: () => void;
  readonly onDone: () => void;
  /**
   * Save a word the learner just met to their deck.
   *
   * The report used to list these as inert chips. The moment a learner has just used
   * five new words out loud is exactly when saving them is worth something, and the
   * deck existed the whole time with nothing pointing at it — Pingo puts a "Save all"
   * right here (docs/research/03-competitor-pingo.md §6) and they are right to.
   */
  readonly onSaveWord?: ((itemId: string, display: string) => void) | undefined;
  /** Which words are already in the deck, so a saved chip reads as saved. */
  readonly savedItemIds?: ReadonlySet<string> | undefined;
}

export function ReportCardScreen({
  report,
  loading,
  onDrill,
  onTranscript,
  onDone,
  onSaveWord,
  savedItemIds,
}: ReportCardScreenProps): JSX.Element {
  if (loading || report === null) {
    return (
      <main id="main" className="vl-page">
        <Skeleton lines={6} label="Building your report card" />
      </main>
    );
  }

  const achieved =
    report.goalAchieved === 'yes'
      ? 'Goal achieved'
      : report.goalAchieved === 'partial'
        ? 'Most of the way there'
        : 'Keep at it';

  return (
    <main id="main" className="vl-page vl-report">
      <h1 className="vl-report__title">{achieved}</h1>

      <ScoreRing score={report.overall} size="lg" label="Overall" />
      <DimensionsPanel
        dimensions={report.dimensions}
        deltas={measuredScores(report.deltas) ?? {}}
      />

      {report.notes.length > 0 && (
        <section className="vl-report__notes" aria-label="What we noticed">
          {report.notes.map((note) => (
            <p key={note.dimension} className="vl-report__note">
              <strong>{note.dimension}:</strong> {note.text}
            </p>
          ))}
        </section>
      )}

      {report.criteria.length > 0 && (
        <section className="vl-report__criteria" aria-label="Lesson goals">
          <ul>
            {report.criteria.map((criterion) => (
              <li key={criterion.id} data-met={criterion.met ? 'true' : 'false'}>
                {criterion.met ? '✓' : '○'} {criterion.description}
              </li>
            ))}
          </ul>
        </section>
      )}

      {report.itemsLearned.length > 0 && (
        <section className="vl-report__items" aria-label="Words you met">
          <ul className="vl-chips">
            {report.itemsLearned.map((item) => {
              const saved = savedItemIds?.has(item.itemId) === true;
              // Inert when there is nowhere to save to, which is how the screenshot
              // tests and any caller without a deck still render.
              if (onSaveWord === undefined) {
                return (
                  <li key={item.itemId} className="vl-chip vl-chip--static">
                    {item.display}
                  </li>
                );
              }
              return (
                <li key={item.itemId}>
                  <button
                    type="button"
                    className="vl-chip vl-chip--action"
                    aria-pressed={saved}
                    onClick={() => onSaveWord(item.itemId, item.display)}
                    aria-label={
                      saved ? `${item.display}, saved to your deck` : `Save ${item.display}`
                    }
                  >
                    {item.display}
                    <span aria-hidden="true">{saved ? ' ✓' : ' +'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {onSaveWord !== undefined &&
            report.itemsLearned.some((item) => savedItemIds?.has(item.itemId) !== true) && (
              <Button
                variant="secondary"
                onClick={() => {
                  for (const item of report.itemsLearned) {
                    if (savedItemIds?.has(item.itemId) !== true) {
                      onSaveWord(item.itemId, item.display);
                    }
                  }
                }}
              >
                Save all to my deck
              </Button>
            )}
        </section>
      )}

      <p className="vl-report__meta">
        {report.turns} turns · {Math.round(report.durationSeconds / 60)} min ·{' '}
        {report.mistakesFound} {report.mistakesFound === 1 ? 'mistake' : 'mistakes'} added to your
        worklist
      </p>

      <div className="vl-report__actions">
        {/* The primary action is always the drill: that is what closes the loop. */}
        <Button size="lg" onClick={onDrill} disabled={report.drillId === null}>
          Drill what you missed
        </Button>
        <Button variant="secondary" onClick={onTranscript}>
          Review the conversation
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </main>
  );
}

/**
 * Plays a recording the API pointed at.
 *
 * Delegates to `playMedia` because the three buttons on this card point at two different
 * kinds of object: the coach readings are public course audio an `<audio>` element can
 * fetch, and "▶ You" is the learner's own recording, which is private and has to be
 * fetched with their token and played as bytes. This used to be `new Audio(url)` for
 * both, so the one button that plays the learner back answered 401 and did nothing.
 */
function playAudio(url: string | null): void {
  void playMedia(url);
}
